import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Modal,
  message,
  Form,
  Select,
  Input,
  DatePicker,
  Switch,
  Tooltip,
  Tabs,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import dayjs from "dayjs";

import { BitmakerAPIClient } from "../../services/BitmakerApp";
import { ContractsData } from "../../bitmaker/schemas/Contracts";
import { ClientsListData } from "../../bitmaker/schemas/Clients";
import { FeesResponse } from "../../bitmaker/schemas/Fees";
import "./styles.scss";

const { Option } = Select;
const { TabPane } = Tabs;

interface ClientResponse {
  status: number;
  data: {
    id: number;
    name: string;
    email: string;
    account: string;
    corporate_name: string;
  };
}

interface ContractResponse {
  status: number;
  data: ContractsData;
}

const CONTRACT_TYPES = [
  { label: "Product", value: "product" },
  { label: "Professional Services", value: "professional_services" },
  { label: "Staff Augmentation", value: "staff_augmentation" },
];

const ContractsList: React.FC = () => {
  const [contracts, setContracts] = useState<ContractsData[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [clients, setClients] = useState<ClientsListData[]>([]);
  const [isNewClient, setIsNewClient] = useState(false);
  const [editingContractId, setEditingContractId] = useState<number | null>(
    null
  );
  const [isFeesModalVisible, setIsFeesModalVisible] = useState(false);
  const [currentFees, setCurrentFees] = useState<FeesResponse | null>(null);
  const [isFeesEditModalVisible, setIsFeesEditModalVisible] = useState(false);
  const [editingFee, setEditingFee] = useState<any>(null);
  const [editingFeeType, setEditingFeeType] = useState<
    "total" | "activity" | "staff_augmentation" | null
  >(null);
  const [feesForm] = Form.useForm();

  const fetchContracts = () => {
    setLoading(true);
    BitmakerAPIClient.get("/contracts", null, (response: any) => {
      setContracts(response.data);
      setLoading(false);
    });
  };

  const fetchClients = () => {
    BitmakerAPIClient.get("/billing/clients", null, (response: any) => {
      setClients(response.data);
    });
  };

  const fetchFees = (contractId: number) => {
    BitmakerAPIClient.get(
      `/fees?contract_id=${contractId}`,
      null,
      (response: any) => {
        if (response.status === 200) {
          setCurrentFees(response.data);
        } else {
          message.error("Failed to fetch fees");
        }
      }
    );
  };

  useEffect(() => {
    fetchContracts();
    fetchClients();
  }, []);

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this contract?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        BitmakerAPIClient.request(
          "DELETE",
          `/contracts/${id}`,
          null,
          (response: any) => {
            if (response.status === 204) {
              message.success("Contract deleted successfully");
              fetchContracts();
            } else {
              message.error("Failed to delete contract");
            }
          }
        );
      },
    });
  };

  const handleEdit = (contract: ContractsData) => {
    setEditingContractId(contract.id);
    form.setFieldsValue({
      sign_date: dayjs(contract.sign_date),
      duration: contract.duration,
      finish_date: contract.finish_date ? dayjs(contract.finish_date) : null,
      value: contract.value,
      type: contract.type,
      client_id: contract.client_id,
      digital_contract: contract.digital_contract,
    });
    setIsModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      let clientId = values.client_id;

      // Si es un nuevo cliente, primero crearlo
      if (isNewClient) {
        const clientData = {
          name: values.client_name,
          email: values.client_email,
          account: values.client_account,
          corporate_name: values.client_corporate_name,
          project_id: 1,
        };

        const clientResponse = await new Promise<ClientResponse>((resolve) => {
          BitmakerAPIClient.post("/billing/clients", clientData, resolve);
        });

        if (clientResponse.status === 201) {
          clientId = clientResponse.data.id;
        } else {
          message.error("Failed to create client");
          return;
        }
      }

      const contractData = {
        sign_date: format(values.sign_date.toDate(), "yyyy-MM-dd"),
        duration: values.duration,
        active: true,
        finish_date: values.finish_date
          ? format(values.finish_date.toDate(), "yyyy-MM-dd")
          : null,
        value: values.value,
        type: values.type,
        client_id: clientId,
        digital_contract: values.digital_contract,
        fees: values.fees || [],
      };

      const isEditing = editingContractId !== null;
      const endpoint = isEditing
        ? `/contracts/${editingContractId}`
        : "/contracts";
      const method = isEditing ? "put" : "post";

      const contractResponse = await new Promise<ContractResponse>(
        (resolve) => {
          BitmakerAPIClient[method](endpoint, contractData, resolve);
        }
      );

      if (contractResponse.status === (isEditing ? 200 : 201)) {
        message.success(
          `Contract ${isEditing ? "updated" : "created"} successfully`
        );
        setIsModalVisible(false);
        form.resetFields();
        setEditingContractId(null);
        setIsNewClient(false);
        fetchContracts();
      } else {
        message.error(`Failed to ${isEditing ? "update" : "create"} contract`);
      }
    } catch (error) {
      message.error(
        `An error occurred while ${
          editingContractId ? "updating" : "creating"
        } the contract`
      );
    }
  };

  const handleViewFees = (contractId: number) => {
    setEditingContractId(contractId);
    fetchFees(contractId);
    setIsFeesModalVisible(true);
  };

  const handleCreateFee = (
    feeType: "total" | "activity" | "staff_augmentation"
  ) => {
    setEditingFee(null);
    setEditingFeeType(feeType);
    feesForm.resetFields();
    setIsFeesEditModalVisible(true);
  };

  const handleEditFee = (
    fee: any,
    feeType: "total" | "activity" | "staff_augmentation"
  ) => {
    setEditingFee(fee);
    setEditingFeeType(feeType);
    feesForm.setFieldsValue({
      ...fee,
      start_date: fee.start_date ? dayjs(fee.start_date) : null,
    });
    setIsFeesEditModalVisible(true);
  };

  const handleDeleteFee = (feeId: number, feeType: string) => {
    Modal.confirm({
      title: "Are you sure you want to delete this fee?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        BitmakerAPIClient.request(
          "DELETE",
          `/fees/${feeType}/${feeId}`,
          null,
          (response: any) => {
            if (response.status === 204) {
              message.success("Fee deleted successfully");
              fetchFees(editingContractId!);
            } else {
              message.error("Failed to delete fee");
            }
          }
        );
      },
    });
  };

  const handleSubmitFee = async (values: any) => {
    const feeData = {
      ...values,
      contract_id: editingContractId,
      start_date: values.start_date
        ? format(values.start_date.toDate(), "yyyy-MM-dd")
        : null,
    };

    const method = editingFee ? "put" : "post";
    const endpoint = editingFee
      ? `/fees/${editingFeeType}/${editingFee.id}`
      : `/fees/${editingFeeType}`;

    BitmakerAPIClient[method](endpoint, feeData, (response: any) => {
      if (response.status === (editingFee ? 200 : 201)) {
        message.success(
          `Fee ${editingFee ? "updated" : "created"} successfully`
        );
        setIsFeesEditModalVisible(false);
        feesForm.resetFields();
        fetchFees(editingContractId!);
      } else {
        message.error(`Failed to ${editingFee ? "update" : "create"} fee`);
      }
    });
  };

  const columns: ColumnsType<ContractsData> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: "descend",
      width: "80px",
    },
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
    },
    {
      title: "Sign Date",
      dataIndex: "sign_date",
      key: "sign_date",
      render: (date: Date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Duration (months)",
      dataIndex: "duration",
      key: "duration",
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      render: (active: boolean) => (
        <span style={{ color: active ? "green" : "red" }}>
          {active ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (value: number | null) =>
        value ? `$${value.toLocaleString()}` : "-",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Actions",
      key: "actions",
      width: "150px",
      render: (_, record) => (
        <Space size="small">
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button
            icon={<DollarOutlined />}
            onClick={() => handleViewFees(record.id)}
          />
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="contracts-list-container">
      <div className="contracts-header">
        <h1>Contracts</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingContractId(null);
            form.resetFields();
            setIsModalVisible(true);
          }}
        >
          Create Contract
        </Button>
      </div>

      <Modal
        title={editingContractId ? "Edit Contract" : "Create Contract"}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingContractId(null);
          setIsNewClient(false);
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!editingContractId && (
            <Form.Item label="Create new client?" name="is_new_client">
              <Switch onChange={(checked) => setIsNewClient(checked)} />
            </Form.Item>
          )}

          {isNewClient && !editingContractId ? (
            <>
              <Form.Item
                label="Client Name"
                name="client_name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Client Email"
                name="client_email"
                rules={[{ required: true, type: "email" }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label={
                  <span>
                    Client Account&nbsp;
                    <Tooltip title="This identifier will be used to track tickets in Redmine">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </span>
                }
                name="client_account"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Corporate Name"
                name="client_corporate_name"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              label="Select Client"
              name="client_id"
              rules={[{ required: true }]}
            >
              <Select disabled={!!editingContractId}>
                {clients.map((client) => (
                  <Option key={client.id} value={client.id}>
                    {client.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Sign Date"
            name="sign_date"
            rules={[{ required: true }]}
          >
            <DatePicker format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item
            label="Duration (months)"
            name="duration"
            rules={[{ required: true }]}
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item label="Value" name="value" rules={[{ required: true }]}>
            <Input type="number" />
          </Form.Item>

          <Form.Item label="Type" name="type" rules={[{ required: true }]}>
            <Select options={CONTRACT_TYPES} />
          </Form.Item>

          <Form.Item label="Digital Contract URL" name="digital_contract">
            <Input />
          </Form.Item>

          <Form.Item label="Finish Date" name="finish_date">
            <DatePicker format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              {editingContractId ? "Update" : "Create"} Contract
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Contract Fees"
        open={isFeesModalVisible}
        onCancel={() => {
          setIsFeesModalVisible(false);
          setEditingContractId(null);
        }}
        footer={null}
        width={800}
      >
        {currentFees && (
          <Tabs defaultActiveKey="1">
            <TabPane tab="Totals" key="1">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleCreateFee("total")}
                style={{ marginBottom: 16 }}
              >
                Add Total
              </Button>
              <Table
                dataSource={currentFees.totals}
                columns={[
                  {
                    title: "Charge",
                    dataIndex: "charge",
                    render: (value: number | null) =>
                      value !== null ? `$${value.toLocaleString()}` : "-",
                  },
                  {
                    title: "Discount",
                    dataIndex: "discount",
                    render: (value: number | null) =>
                      value !== null ? `$${value.toLocaleString()}` : "-",
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, record) => (
                      <Space>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => handleEditFee(record, "total")}
                        />
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteFee(record.id, "total")}
                        />
                      </Space>
                    ),
                  },
                ]}
              />
            </TabPane>
            <TabPane tab="Activities" key="2">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleCreateFee("activity")}
                style={{ marginBottom: 16 }}
              >
                Add Activity
              </Button>
              <Table
                dataSource={currentFees.activities}
                columns={[
                  {
                    title: "Start Date",
                    dataIndex: "start_date",
                    render: (date: string) =>
                      date ? new Date(date).toLocaleDateString() : "-",
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                  },
                  {
                    title: "Hours",
                    dataIndex: "hours",
                    render: (value: number | null) =>
                      value !== null ? value : "-",
                  },
                  {
                    title: "Charge",
                    dataIndex: "charge",
                    render: (value: number | null) =>
                      value !== null ? `$${value.toLocaleString()}` : "-",
                  },
                  {
                    title: "Discount",
                    dataIndex: "discount",
                    render: (value: number | null) =>
                      value !== null ? `$${value.toLocaleString()}` : "-",
                  },
                  {
                    title: "Recurring",
                    dataIndex: "recurring",
                    render: (recurring: boolean) => (recurring ? "Yes" : "No"),
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, record) => (
                      <Space>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() => handleEditFee(record, "activity")}
                        />
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteFee(record.id, "activity")}
                        />
                      </Space>
                    ),
                  },
                ]}
              />
            </TabPane>
            <TabPane tab="Staff Augmentation" key="3">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleCreateFee("staff_augmentation")}
                style={{ marginBottom: 16 }}
              >
                Add Staff Augmentation
              </Button>
              <Table
                dataSource={currentFees.staff_augmentations}
                columns={[
                  {
                    title: "Start Date",
                    dataIndex: "start_date",
                    render: (date: string) =>
                      date ? new Date(date).toLocaleDateString() : "-",
                  },
                  {
                    title: "Description",
                    dataIndex: "description",
                  },
                  {
                    title: "Hours",
                    dataIndex: "hours",
                    render: (value: number | null) =>
                      value !== null ? value : "-",
                  },
                  {
                    title: "Charge",
                    dataIndex: "charge",
                    render: (value: number | null) =>
                      value !== null ? `$${value.toLocaleString()}` : "-",
                  },
                  {
                    title: "Discount",
                    dataIndex: "discount",
                    render: (value: number | null) =>
                      value !== null ? `$${value.toLocaleString()}` : "-",
                  },
                  {
                    title: "User ID",
                    dataIndex: "user_id",
                    render: (value: number | null) =>
                      value !== null ? value : "-",
                  },
                  {
                    title: "Actions",
                    key: "actions",
                    render: (_, record) => (
                      <Space>
                        <Button
                          icon={<EditOutlined />}
                          onClick={() =>
                            handleEditFee(record, "staff_augmentation")
                          }
                        />
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() =>
                            handleDeleteFee(record.id, "staff_augmentation")
                          }
                        />
                      </Space>
                    ),
                  },
                ]}
              />
            </TabPane>
          </Tabs>
        )}
      </Modal>

      <Modal
        title={`${editingFee ? "Edit" : "Create"} ${editingFeeType
          ?.replace("_", " ")
          .toUpperCase()}`}
        open={isFeesEditModalVisible}
        onCancel={() => setIsFeesEditModalVisible(false)}
        footer={null}
      >
        <Form form={feesForm} layout="vertical" onFinish={handleSubmitFee}>
          {editingFeeType === "total" && (
            <>
              <Form.Item
                label="Charge"
                name="charge"
                rules={[{ required: true }]}
              >
                <Input type="number" />
              </Form.Item>
              <Form.Item label="Discount" name="discount">
                <Input type="number" />
              </Form.Item>
            </>
          )}

          {(editingFeeType === "activity" ||
            editingFeeType === "staff_augmentation") && (
            <>
              <Form.Item label="Start Date" name="start_date">
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Hours"
                name="hours"
                rules={[{ required: true }]}
              >
                <Input type="number" />
              </Form.Item>
              <Form.Item
                label="Charge"
                name="charge"
                rules={[{ required: true }]}
              >
                <Input type="number" />
              </Form.Item>
              <Form.Item label="Discount" name="discount">
                <Input type="number" />
              </Form.Item>
              {editingFeeType === "activity" && (
                <Form.Item
                  label="Recurring"
                  name="recurring"
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              )}
              {editingFeeType === "staff_augmentation" && (
                <Form.Item
                  label="User ID"
                  name="user_id"
                  rules={[{ required: true }]}
                >
                  <Input type="number" />
                </Form.Item>
              )}
            </>
          )}

          <Form.Item>
            <Button type="primary" htmlType="submit">
              {editingFee ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Table
        columns={columns}
        dataSource={contracts}
        loading={loading}
        rowKey="id"
        sticky={{ offsetHeader: 0 }}
      />
    </div>
  );
};

export default ContractsList;
